import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Menu, X } from 'lucide-react';
import { PUBLIC_CONTAINER } from '@/components/PublicContainer';

/**
 * Single canonical public-site header used by the homepage and every public content page.
 * Full primary nav on desktop, hamburger below lg. Homepage-section links use "/#id" and the
 * centralized ScrollManager performs the actual navigation+scroll (works from any route and
 * when already on the homepage), so links here are plain router Links — no per-page scroll code.
 */
const NAV_ANCHORS = [
  { label: 'Service Members', id: 'service-members' },
  { label: 'Employers', id: 'employers' },
  { label: 'Communities', id: 'communities' },
  { label: 'Why Sergeant TAP', id: 'why-sergeant-tap' },
];

export const PublicHeader = () => {
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);

  const linkCls = 'text-sm font-semibold text-[#444] hover:text-brand transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-brand rounded';

  return (
    <header className="w-full bg-white border-b border-[#e5e5e5] relative z-40" data-testid="public-header">
      <div className={`${PUBLIC_CONTAINER} flex items-center justify-between h-16 gap-4`}>
        <Link to="/" className="flex items-center gap-2.5 flex-shrink-0" data-testid="logo-home-link">
          <img src={`${process.env.PUBLIC_URL}/sergeant-tap-logo.png`} alt="Sergeant TAP" className="h-9 w-auto object-contain" />
        </Link>

        {/* Desktop primary nav (>= lg) */}
        <nav aria-label="Primary" className="hidden lg:flex items-center gap-6">
          {NAV_ANCHORS.map((l) => (
            <Link key={l.id} to={`/#${l.id}`} data-testid={`nav-${l.id}`} className={linkCls}>
              {l.label}
            </Link>
          ))}
          <Link to="/about" data-testid="nav-about" className={linkCls}>About</Link>
        </nav>

        <div className="flex items-center gap-2 flex-shrink-0">
          <button
            data-testid="navbar-login-btn"
            onClick={() => navigate('/auth')}
            className="btn-uppercase bg-brand hover:bg-brand-hover text-white px-5 py-2.5 rounded transition-colors"
          >
            Sign In
          </button>
          {/* Hamburger (< lg) */}
          <button
            type="button"
            aria-label="Open menu"
            data-testid="public-mobile-menu-btn"
            onClick={() => setMenuOpen((v) => !v)}
            className="lg:hidden p-2 text-[#444] hover:text-brand"
          >
            {menuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {/* Mobile menu panel */}
      {menuOpen && (
        <div className="lg:hidden border-t border-[#e5e5e5] bg-white shadow-sm" data-testid="public-mobile-menu">
          <nav aria-label="Mobile" className={`${PUBLIC_CONTAINER} py-3 flex flex-col`}>
            {NAV_ANCHORS.map((l) => (
              <Link key={l.id} to={`/#${l.id}`} data-testid={`nav-${l.id}-m`} onClick={() => setMenuOpen(false)}
                className="py-3 text-sm font-semibold text-[#444] hover:text-brand border-b border-slate-100">
                {l.label}
              </Link>
            ))}
            <Link to="/about" data-testid="nav-about-m" onClick={() => setMenuOpen(false)}
              className="py-3 text-sm font-semibold text-[#444] hover:text-brand border-b border-slate-100">About</Link>
            <Link to="/news-research" data-testid="nav-news-m" onClick={() => setMenuOpen(false)}
              className="py-3 text-sm font-semibold text-[#444] hover:text-brand border-b border-slate-100">News &amp; Research</Link>
            <Link to="/contact" data-testid="nav-contact-m" onClick={() => setMenuOpen(false)}
              className="py-3 text-sm font-semibold text-[#444] hover:text-brand">Contact</Link>
          </nav>
        </div>
      )}
    </header>
  );
};

export default PublicHeader;
